import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { defaultDayRange } from '../utils/reviewDateFilterOptions';

export type ReviewStatusFilter = 'all' | 'pending' | 'ready' | 'pending-mentions';

interface FilterState {
  // Time period filter for analysis review (jan, feb, mar, etc.)
  timePeriod: string;
  setTimePeriod: (timePeriod: string) => void;
  
  // Month filter for content analysis (YYYY-MM format like 2025-01)
  selectedMonth: string;
  setSelectedMonth: (selectedMonth: string) => void;
  
  // Week filter for content analysis + review list/detail period (YYYY-MM-DD or start,end)
  selectedWeek: string;
  setSelectedWeek: (selectedWeek: string) => void;

  /** By week / month / day for review date period (shared `/review` + `/review/:id`). */
  reviewDateFilterMode: 'week' | 'month' | 'day';
  setReviewDateFilterMode: (mode: 'week' | 'month' | 'day') => void;

  /** Episode review status filter on `/review/:podcastId`. */
  reviewStatusFilter: ReviewStatusFilter;
  setReviewStatusFilter: (filter: ReviewStatusFilter) => void;
  
  // Program filter for content analysis
  selectedProgram: string;
  setSelectedProgram: (selectedProgram: string) => void;
  
  // Brand search query for content analysis
  brandSearchQuery: string;
  setBrandSearchQuery: (brandSearchQuery: string) => void;
  
  // Active tab in analysis review (0 = Podcasts, 1 = Brands, 2 = Topics)
  activeTab: number;
  setActiveTab: (activeTab: number) => void;
  
  // Reset all filters to defaults
  resetFilters: () => void;
}

export const useFilterStore = create<FilterState>()(
  persist(
    (set) => ({
      timePeriod: 'all',
      selectedMonth: 'all',
      selectedWeek: 'all',
      reviewDateFilterMode: 'week',
      reviewStatusFilter: 'pending',
      selectedProgram: 'all',
      brandSearchQuery: '',
      activeTab: 0,
      setTimePeriod: (timePeriod: string) => set({ timePeriod }),
      setSelectedMonth: (selectedMonth: string) => set({ selectedMonth }),
      setSelectedWeek: (selectedWeek: string) => set({ selectedWeek }),
      setReviewDateFilterMode: (reviewDateFilterMode: 'week' | 'month' | 'day') =>
        set({ reviewDateFilterMode }),
      setReviewStatusFilter: (reviewStatusFilter: ReviewStatusFilter) =>
        set({ reviewStatusFilter }),
      setSelectedProgram: (selectedProgram: string) => set({ selectedProgram }),
      setBrandSearchQuery: (brandSearchQuery: string) => set({ brandSearchQuery }),
      setActiveTab: (activeTab: number) => set({ activeTab }),
      resetFilters: () =>
        set({
          timePeriod: 'all',
          selectedMonth: 'all',
          selectedWeek: 'all',
          reviewDateFilterMode: 'week',
          reviewStatusFilter: 'pending',
          selectedProgram: 'all',
          brandSearchQuery: '',
          activeTab: 0,
        }),
    }),
    {
      name: 'podcast-analysis-filters',
      version: 6,
      migrate: (persistedState, version) => {
        const s = (persistedState ?? {}) as Partial<FilterState>;
        if (version < 6) {
          return {
            ...s,
            reviewDateFilterMode: s.reviewDateFilterMode ?? 'week',
            reviewStatusFilter: s.reviewStatusFilter ?? 'pending',
          } as FilterState;
        }
        return persistedState as FilterState;
      },
    }
  )
);

/** When switching review date mode, pick a sensible default period value. */
export function initialSelectedWeekForReviewMode(
  mode: 'week' | 'month' | 'day',
): string {
  if (mode === 'day') return defaultDayRange();
  return 'all';
}
